import { forwardRef, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';

const SearchInputWithSuggestions = forwardRef(
  (
    {
      searchTerm,
      placeholder = 'Digite o nome ou o usuário...',
      disabled = false,
      suggestions = [],
      showSuggestions = false,
      onSearchChange,
      onSuggestionSelect,
      renderSuggestionItem,
      maxHeight = '200px',
      className = '',
      size = 'md',
      required = false,
      inputProps = {}
    },
    ref
  ) => {
    const suggestionRef = useRef(null);
    const containerRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState('bottom');

    const defaultRenderSuggestionItem = (item, index) => (
      <div
        key={item.id || index}
        className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
        onClick={() => onSuggestionSelect(item)}
        role="button"
        style={{
          transition: 'background-color 0.2s'
        }}
      >
        <div className="fw-bold">{item.name || item.title || item.label}</div>
        <div className="text-muted small">
          {item.subtitle || item.description || ''}
        </div>
      </div>
    );

    // Calculate dropdown position based on available space
    const calculateDropdownPosition = () => {
      if (!containerRef.current || !showSuggestions) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = parseInt(maxHeight) || 200;

      // Space available below the input
      const spaceBelow = viewportHeight - rect.bottom;
      // Space available above the input
      const spaceAbove = rect.top;

      // If there's not enough space below but enough space above, show on top
      if (spaceBelow < dropdownMaxHeight && spaceAbove > dropdownMaxHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    };

    // Recalculate position when suggestions visibility changes
    useEffect(() => {
      if (showSuggestions) {
        calculateDropdownPosition();

        // Recalculate on window resize
        const handleResize = () => calculateDropdownPosition();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
      }
    }, [showSuggestions, maxHeight]);

    // Get dropdown styles based on position
    const getDropdownStyles = () => {
      const baseStyles = {
        zIndex: 1000,
        maxHeight: maxHeight,
        overflowY: 'auto'
      };

      if (dropdownPosition === 'top') {
        return {
          ...baseStyles,
          bottom: '100%',
          borderTop: '1px solid #dee2e6',
          borderBottom: 'none'
        };
      } else {
        return {
          ...baseStyles,
          top: '100%'
        };
      }
    };

    return (
      <div ref={containerRef} className={`position-relative ${className}`}>
        <Form.Control
          type="text"
          placeholder={placeholder}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          value={searchTerm}
          disabled={disabled}
          onChange={onSearchChange}
          size={size}
          required={required}
          ref={ref}
          {...inputProps}
        />

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionRef}
            className={`w-100 bg-100 border shadow-sm position-absolute ${
              dropdownPosition === 'top' ? 'border-bottom-0' : 'border-top-0'
            }`}
            style={getDropdownStyles()}
          >
            {suggestions.map((suggestion, index) =>
              renderSuggestionItem
                ? renderSuggestionItem(suggestion, index)
                : defaultRenderSuggestionItem(suggestion, index)
            )}
          </div>
        )}
      </div>
    );
  }
);

SearchInputWithSuggestions.displayName = 'SearchInputWithSuggestions';

export default SearchInputWithSuggestions;
